/**
 * Property Data Helper
 * Provides backward-compatible access to property data
 * Handles migration from property_data to property_snapshot
 */

/**
 * Get property data from query object
 * Supports both old (property_data) and new (property_snapshot) formats
 * 
 * @param {Object} query - Query object from API
 * @returns {Object} Property data object
 */
export const getPropertyData = (query) => {
  if (!query) return {};
  
  // Try property_snapshot first (new format)
  if (query.property_snapshot) {
    return query.property_snapshot;
  }
  
  // Fall back to property_data (backward compatibility)
  if (query.property_data) {
    return query.property_data;
  }
  
  // Last resort: construct from individual columns
  return {
    address: query.property_address,
    city: query.property_city,
    state: query.property_state,
    zip_code: query.property_zip,
    building_square_feet: query.building_square_feet,
    lot_square_feet: query.lot_square_feet,
    estimated_value: query.estimated_value,
    estimated_rent_amount: query.estimated_rent_amount,
  };
};

/**
 * Get property field value
 * Tries multiple sources in order: property_snapshot, property_data, individual column
 * 
 * @param {Object} query - Query object
 * @param {string} field - Field name
 * @param {*} defaultValue - Default value if not found
 * @returns {*} Field value
 */
export const getPropertyField = (query, field, defaultValue = null) => {
  if (!query) return defaultValue;
  
  // Try property_snapshot
  if (query.property_snapshot && query.property_snapshot[field] !== undefined) {
    return query.property_snapshot[field];
  }
  
  // Try property_data (backward compatibility)
  if (query.property_data && query.property_data[field] !== undefined) {
    return query.property_data[field];
  }
  
  // Try individual column (with snake_case and camelCase)
  const snakeCase = field.replace(/([A-Z])/g, '_$1').toLowerCase();
  if (query[field] !== undefined) {
    return query[field];
  }
  if (query[snakeCase] !== undefined) {
    return query[snakeCase];
  }
  
  return defaultValue;
};

/**
 * Field mapping for common property fields
 */
export const PROPERTY_FIELDS = {
  address: 'address',
  city: 'city',
  state: 'state',
  zipCode: 'zip_code',
  buildingSquareFeet: 'building_square_feet',
  lotSquareFeet: 'lot_square_feet',
  estimatedValue: 'estimated_value',
  estimatedRentAmount: 'estimated_rent_amount',
  marketValuePerSqft: 'market_value_per_sqft',
  loanBalance: 'loan_balance',
  pool: 'pool',
  bedrooms: 'bedrooms',
  bathrooms: 'bathrooms',
  yearBuilt: 'year_built',
  propertyType: 'property_type',
};

export default {
  getPropertyData,
  getPropertyField,
  PROPERTY_FIELDS,
};
